import React, { useState, useEffect } from 'react';
import { useLang } from '../../contexts/LanguageContext';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';

const AVAILABLE_MODULES = [
  'Knowledge Pack Manager',
  'Studios',
  'Nexus Academy',
  'Analytics',
  'Queue',
  'Communications'
];

const CreateFunctionalGroupWizard = ({ onClose, onSuccess }) => {
  const t = useLang();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    purpose_tag: '',
    description: '',
    allowed_modules: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [purposeTags, setPurposeTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(true);

  // Fetch purpose tags
  useEffect(() => {
    const fetchPurposeTags = async () => {
      try {
        const token = secureStorage.getItem('nexus_access_token');
        const response = await fetch(
          `${BACKEND_URL}/api/admin-panel/purpose-tags`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.ok) {
          const tags = await response.json();
          setPurposeTags(tags);
        }
      } catch (error) {
        console.error('Error fetching purpose tags:', error);
      } finally {
        setLoadingTags(false);
      }
    };
    
    fetchPurposeTags();
  }, []);

  // Auto-save draft every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      localStorage.setItem('admin_group_draft', JSON.stringify(formData));
    }, 5000);

    return () => clearInterval(timer);
  }, [formData]);

  // Load draft on mount
  useEffect(() => {
    const draft = localStorage.getItem('admin_group_draft');
    if (draft) {
      try {
        setFormData(JSON.parse(draft));
      } catch (e) {
        // Ignore invalid draft
      }
    }
  }, []);

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');

    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/functional-groups`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }
      );

      const result = await response.json();

      if (response.ok) {
        // Clear draft
        localStorage.removeItem('admin_group_draft');
        
        // Show success message
        alert(`✅ ${t('admin.functionalGroups.groupCreated')}\n\n${t('admin.audit.logged')}`);
        
        onSuccess();
      } else {
        setError(result.detail || t('admin.error.createFailed'));
      }
    } catch (err) {
      setError(t('admin.error.networkError'));
    } finally {
      setSaving(false);
    }
  };

  const toggleModule = (module) => {
    setFormData(prev => ({
      ...prev,
      allowed_modules: prev.allowed_modules.includes(module)
        ? prev.allowed_modules.filter(m => m !== module)
        : [...prev.allowed_modules, module]
    }));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('admin.functionalGroups.groupName')} *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('admin.functionalGroups.namePlaceholder')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('admin.functionalGroups.purposeTag')} *
              </label>
              <select
                value={formData.purpose_tag}
                onChange={(e) => setFormData({ ...formData, purpose_tag: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loadingTags}
              >
                <option value="">
                  {loadingTags ? 'Loading tags...' : t('admin.functionalGroups.selectPurpose')}
                </option>
                {purposeTags.map((tag) => (
                  <option key={tag.tag_id} value={tag.name}>
                    {tag.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('admin.functionalGroups.description')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('admin.functionalGroups.descriptionPlaceholder')}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="text-4xl mb-2">👥</div>
              <p className="text-sm text-gray-600">
                {t('admin.functionalGroups.roleAssignmentInfo')}
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-sm text-blue-900 mb-3">
                {t('admin.functionalGroups.roleHierarchy')}
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-blue-600 rounded-full"></span>
                  <span className="font-semibold">Management:</span>
                  <span className="text-gray-700">{t('admin.functionalGroups.managementDesc')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                  <span className="font-semibold">Supervisor:</span>
                  <span className="text-gray-700">{t('admin.functionalGroups.supervisorDesc')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  <span className="font-semibold">Executor:</span>
                  <span className="text-gray-700">{t('admin.functionalGroups.executorDesc')}</span>
                </div>
              </div>
            </div>

            <div className="text-xs text-gray-500 text-center">
              {t('admin.functionalGroups.roleNote')}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="bg-orange-50 border-l-4 border-orange-500 p-3 rounded-r-lg">
              <p className="text-sm text-orange-800">
                💡 {t('admin.functionalGroups.moduleAccessInfo')}
              </p>
            </div>

            <div className="space-y-2">
              {AVAILABLE_MODULES.map((module) => (
                <label
                  key={module}
                  className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-blue-300 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={formData.allowed_modules.includes(module)}
                    onChange={() => toggleModule(module)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-gray-900">
                      {module}
                    </div>
                  </div>
                  {formData.allowed_modules.includes(module) && (
                    <span className="text-green-500">✓</span>
                  )}
                </label>
              ))}
            </div>

            <div className="text-xs text-gray-500">
              {t('admin.functionalGroups.selectedModules')}: {formData.allowed_modules.length}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="text-center py-2">
              <div className="text-4xl mb-2">✅</div>
              <h3 className="text-lg font-bold text-gray-900">
                {t('admin.functionalGroups.reviewTitle')}
              </h3>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-3 text-sm">
              <div>
                <div className="text-gray-600 mb-1">{t('admin.functionalGroups.groupName')}:</div>
                <div className="font-semibold text-gray-900">{formData.name}</div>
              </div>

              <div>
                <div className="text-gray-600 mb-1">{t('admin.functionalGroups.purposeTag')}:</div>
                <div className="font-semibold text-gray-900">{formData.purpose_tag}</div>
              </div>

              {formData.description && (
                <div>
                  <div className="text-gray-600 mb-1">{t('admin.functionalGroups.description')}:</div>
                  <div className="text-gray-700">{formData.description}</div>
                </div>
              )}

              <div>
                <div className="text-gray-600 mb-1">{t('admin.functionalGroups.allowedModules')}:</div>
                <div className="flex flex-wrap gap-1">
                  {formData.allowed_modules.map((module) => (
                    <span
                      key={module}
                      className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                    >
                      {module}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-r-lg">
              <p className="text-sm text-green-800">
                🔒 {t('admin.audit.willBeLogged')}
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-r-lg">
                <p className="text-sm text-red-800">❌ {error}</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.name && formData.purpose_tag;
      case 2:
        return true; // Info step
      case 3:
        return formData.allowed_modules.length > 0;
      case 4:
        return true; // Review step
      default:
        return false;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold">
                {t('admin.functionalGroups.createWizard')}
              </h2>
              <p className="text-sm text-blue-100 mt-1">
                {t('admin.functionalGroups.step')} {currentStep} {t('admin.functionalGroups.of')} 4
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-gray-200 h-1">
          <div
            className="bg-blue-600 h-1 transition-all duration-300"
            style={{ width: `${(currentStep / 4) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderStep()}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex justify-between gap-2">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← {t('common.back')}
          </button>

          {currentStep < 4 ? (
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {t('common.next')} →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={saving || !canProceed()}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center gap-2"
            >
              {saving ? (
                <>
                  <span className="animate-spin">⏳</span>
                  {t('common.saving')}...
                </>
              ) : (
                <>
                  ✓ {t('admin.functionalGroups.createGroup')}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateFunctionalGroupWizard;
